import { useState, useMemo, useEffect, useRef } from "react";

/**
 * Manages which tool panel is currently open in the FDC page.
 * Replaces 6 individual useState booleans with a single active-tool state.
 *
 * On desktop the map auto-opens when a mission becomes available so the operator
 * always has a spatial view. On mobile we leave it closed — the dock takes most of
 * the viewport and the operator should choose to open it.
 */
export default function useToolPanels(hasMission, { autoOpenMap = true } = {}) {
  // Map (with the offline base-map picker) is the default open tool so the operator
  // always has a spatial view and can switch base maps, even before a mission exists.
  const [activeTool, setActiveTool] = useState(autoOpenMap ? "map" : null);
  const userClosedRef = useRef(false);

  // Auto-open map when a mission becomes available, unless the user explicitly
  // closed it OR the caller has disabled auto-open (e.g. on mobile).
  useEffect(() => {
    if (autoOpenMap && activeTool === null && !userClosedRef.current) {
      setActiveTool("map");
    }
  }, [activeTool, autoOpenMap]);

  const showMapPanel = activeTool === "map";
  const showPatternPanel = activeTool === "pattern";
  const showBatteryTool = activeTool === "battery";
  const showFireRegTool = activeTool === "fireReg";
  const showCreateMissionTool = activeTool === "createMission";
  const showEditMissionTool = activeTool === "editMission";
  const showSafetyTool = activeTool === "safety";

  const toolsOpen = activeTool !== null;

  const toolOptions = useMemo(
    () => [
      { value: "createMission", label: "New Mission", mobileLabel: "New", disabled: false },
      { value: "editMission", label: "Edit Mission", mobileLabel: "Edit", disabled: !hasMission },
      { value: "map", label: "Map", mobileLabel: "Map", disabled: false },
      { value: "pattern", label: "Pattern", mobileLabel: "Pattern", disabled: !hasMission },
      { value: "battery", label: "Guns", mobileLabel: "Guns", disabled: !hasMission },
      { value: "fireReg", label: "Fire Reg", mobileLabel: "Fire Reg", disabled: !hasMission },
      { value: "safety", label: "Safety", mobileLabel: "Safety", disabled: !hasMission },
    ],
    [hasMission],
  );

  const activeToolLabel =
    toolOptions.find((tool) => tool.value === activeTool)?.label || "Tools";

  const getDefaultToolValue = () => {
    if (!hasMission) return "createMission";
    return activeTool || "map";
  };

  const openTool = (toolValue) => {
    if (toolValue) setActiveTool(toolValue);
  };

  const handleToolChange = (_e, value) => {
    if (value) setActiveTool(value);
  };

  const closeAllTools = () => {
    userClosedRef.current = true;
    setActiveTool(null);
  };

  return {
    activeTool,
    toolsOpen,
    toolOptions,
    activeToolLabel,
    showMapPanel,
    showPatternPanel,
    showBatteryTool,
    showFireRegTool,
    showCreateMissionTool,
    showEditMissionTool,
    showSafetyTool,
    getDefaultToolValue,
    openTool,
    handleToolChange,
    closeAllTools,
  };
}
